/**
 * InitiativeService - Initiative Management Service
 * 
 * Handles all initiative operations:
 * - CRUD operations (Create, Read, Update, Delete)
 * - Transfer from chat
 * - Validation and storage
 * - Status tracking
 * - Analytics
 * 
 * @module services/InitiativeService
 */

import AppState from '../core/AppState.js';
import { actions, dispatch as storeDispatch } from '../store/index.js';
import EventBus, { AppEvents } from '../core/EventBus.js';
import Config from '../core/Config.js';
import StorageService from './StorageService.js';
import initiativesRepo from './repo/InitiativesRepo.js';
import ValidationService from './ValidationService.js';

/**
 * Initiative status types
 */
const INITIATIVE_STATUS = {
  NOT_STARTED: 'not-started',
  IN_PROGRESS: 'in-progress',
  AT_RISK: 'at-risk',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

/**
 * Initiative priority levels
 */
const PRIORITY_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

class InitiativeServiceClass {
  constructor() {
    this.initiatives = [];
    this.currentInitiative = null;
    this.filters = {
      status: null,
      owner: null,
      strategicAlignment: null,
      priority: null
    };
    this.sortBy = 'createdAt';
    this.sortOrder = 'desc';
    this.initialized = false;
  }
  
  /**
   * Initialize the service
   */
  async initialize() {
    console.log('🎯 Initializing InitiativeService...');
    
    // Load initiatives from repository (centralized persistence)
    await this.loadInitiatives();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Subscribe to state changes
    AppState.subscribe('initiatives', (initiatives) => {
      this.initiatives = initiatives;
    });
    
    this.initialized = true;
    console.log(`✅ InitiativeService initialized with ${this.initiatives.length} initiatives`);
  }
  
  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Listen for transfer from chat
    EventBus.on(AppEvents.INITIATIVE_TRANSFER, (data) => {
      this.handleTransferFromChat(data);
    });
    
    // Listen for initiative updates
    EventBus.on('initiative:update', ({ id, updates }) => {
      this.updateInitiative(id, updates);
    });
    
    // Listen for delete requests
    EventBus.on('initiative:delete', (id) => {
      this.deleteInitiative(id);
    });
  }
  
  /**
   * Load initiatives from storage
   */
  async loadInitiatives() {
    try {
      let stored = [];
      const useIDB = (() => {
        try {
          if (typeof window !== 'undefined' && window.__USE_INDEXED_DB__) return true;
          if (typeof localStorage !== 'undefined' && localStorage.getItem('sip_useIndexedDb') === 'true') return true;
        } catch(_) {}
        try { return import.meta.env?.VITE_USE_INDEXEDDB === 'true'; } catch(_) { return false; }
      })();

      stored = useIDB ? await initiativesRepo.loadIndexed() : initiativesRepo.load();
      
      // Validate and clean data
      const validated = [];
      for (const initiative of stored) {
        const validation = ValidationService.validate(initiative, 'initiative');
        if (validation.valid) {
          validated.push({
            ...initiative,
            id: initiative.id || this.generateId()
          });
        } else {
          console.warn('Invalid initiative found in storage:', validation.errors);
        }
      }
      
      this.initiatives = validated;
      // Analytics: record DB load
      try {
        const mod = await import('./AnalyticsService.js');
        mod?.default?.logDbLoad?.('initiatives', { count: this.initiatives.length });
      } catch (_) {}
      
      // Update centralized store (fallback to AppState)
      try { storeDispatch(actions.setInitiatives(this.initiatives)); }
      catch (_) { AppState.setState({ initiatives: this.initiatives }); }
      
      console.log(`📦 Loaded ${this.initiatives.length} initiatives from repository`);
    } catch (error) {
      console.error('Failed to load initiatives:', error);
      this.initiatives = [];
    }
  }
  
  /**
   * Save initiatives to storage
   */
  async saveInitiatives() {
    try {
      const saved = initiativesRepo.save(this.initiatives);
      EventBus.emit(AppEvents.INITIATIVES_SAVED, { count: this.initiatives.length });
      return saved;
    } catch (error) {
      console.error('Failed to save initiatives:', error);
      return false;
    }
  }
  
  /**
   * Create a new initiative
   * @param {Object} data - Initiative data
   * @returns {Object} Created initiative
   */
  async createInitiative(data) {
    // Sanitize and validate data
    const clean = this.sanitize(data);
    const validation = ValidationService.validateInitiative(clean);
    if (!validation.valid) {
      throw new Error(`Validation failed: ${ValidationService.getErrorsAsString()}`);
    }
    
    // Create initiative object
    const initiative = {
      id: this.generateId(),
      ...validation.data,
      status: data.status || INITIATIVE_STATUS.NOT_STARTED,
      priority: data.priority || PRIORITY_LEVELS.MEDIUM,
      progress: data.progress || 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: data.createdBy || 'user',
      tags: data.tags || [],
      metrics: data.metrics || [],
      risks: data.risks || [],
      dependencies: data.dependencies || [],
      attachments: data.attachments || []
    };
    
    // Add to collection
    this.initiatives.push(initiative);
    
    // Update centralized store (fallback to AppState)
    try { storeDispatch(actions.setInitiatives(this.initiatives)); }
    catch (_) { AppState.setState({ initiatives: this.initiatives }); }
    
    // Save to storage
    await this.saveInitiatives();

    await syncImpactMapIfNeeded(initiative, null);

    // Emit event
    EventBus.emit(AppEvents.INITIATIVE_ADDED, initiative);
    
    console.log('✅ Initiative created:', initiative.title);
    
    return initiative;
  }
  
  /**
   * Update an initiative
   * @param {string} id - Initiative ID
   * @param {Object} updates - Updates to apply
   * @returns {Object} Updated initiative
   */
  async updateInitiative(id, updates) {
    const index = this.initiatives.findIndex(i => i.id === id);
    if (index === -1) {
      throw new Error(`Initiative not found: ${id}`);
    }
    
    // Get current initiative
    const current = this.initiatives[index];
    
    // Merge updates
    const updated = {
      ...current,
      ...this.sanitize(updates),
      id: current.id, // Preserve ID
      createdAt: current.createdAt, // Preserve creation date
      updatedAt: new Date().toISOString()
    };
    
    // Validate if substantial changes
    if (updates.title || updates.description || updates.objective || updates.keyResults) {
      const validation = ValidationService.validateInitiative(updated);
      if (!validation.valid) {
        throw new Error(`Validation failed: ${ValidationService.getErrorsAsString()}`);
      }
    }
    
    // Update in collection
    this.initiatives[index] = updated;
    
    // Update centralized store (fallback to AppState)
    try { storeDispatch(actions.setInitiatives(this.initiatives)); }
    catch (_) { AppState.setState({ initiatives: this.initiatives }); }
    
    // Save to storage
    await this.saveInitiatives();

    await syncImpactMapIfNeeded(updated, current);

    // Emit event
    EventBus.emit(AppEvents.INITIATIVE_UPDATED, updated);
    
    console.log('✅ Initiative updated:', updated.title);
    
    return updated;
  }

  /**
   * Sanitize initiative data (strip unknown or deprecated fields)
   */
  sanitize(data = {}) {
    const clone = { ...data };
    // Remove deprecated fields
    delete clone.timeframe;
    return clone;
  }
  
  /**
   * Delete an initiative
   * @param {string} id - Initiative ID
   * @returns {boolean} Success
   */
  async deleteInitiative(id) {
    const index = this.initiatives.findIndex(i => i.id === id);
    if (index === -1) {
      throw new Error(`Initiative not found: ${id}`);
    }
    
    // Get initiative for event
    const deleted = this.initiatives[index];
    
    // Remove from collection
    this.initiatives.splice(index, 1);
    
    // Update centralized store (fallback to AppState)
    try { storeDispatch(actions.setInitiatives(this.initiatives)); }
    catch (_) { AppState.setState({ initiatives: this.initiatives }); }
    
    // Save to storage
    await this.saveInitiatives();
    
    // Emit event
    EventBus.emit(AppEvents.INITIATIVE_DELETED, deleted);
    
    console.log('✅ Initiative deleted:', deleted.title);
    
    return true;
  }
  
  /**
   * Get initiative by ID
   * @param {string} id - Initiative ID
   * @returns {Object} Initiative
   */
  getInitiative(id) {
    return this.initiatives.find(i => i.id === id);
  }
  
  /**
   * Get all initiatives
   * @param {Object} options - Query options
   * @returns {Array} Initiatives
   */
  getInitiatives(options = {}) {
    let results = [...this.initiatives];
    
    // Apply filters
    if (options.status) {
      results = results.filter(i => i.status === options.status);
    }
    if (options.owner) {
      results = results.filter(i => i.owner === options.owner);
    }
    if (options.priority) {
      results = results.filter(i => i.priority === options.priority);
    }
    if (options.strategicAlignment) {
      results = results.filter(i => i.strategicAlignment === options.strategicAlignment);
    }
    if (options.search) {
      const search = options.search.toLowerCase();
      results = results.filter(i => 
        i.title.toLowerCase().includes(search) ||
        i.description.toLowerCase().includes(search) ||
        i.objective.toLowerCase().includes(search)
      );
    }
    
    // Apply sorting
    const sortBy = options.sortBy || this.sortBy;
    const sortOrder = options.sortOrder || this.sortOrder;
    
    results.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Handle dates
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      // Handle strings
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    // Apply pagination
    if (options.limit) {
      const offset = options.offset || 0;
      results = results.slice(offset, offset + options.limit);
    }
    
    return results;
  }
  
  /**
   * Handle transfer from chat
   * @param {Object} data - Transfer data from chat
   */
  async handleTransferFromChat(data) {
    console.log('📥 Handling transfer from chat:', data);
    
    try {
      // Format data for initiative
      const initiativeData = {
        title: data.title || 'New Initiative from Chat',
        description: data.description || '',
        objective: data.objective || data.title,
        keyResults: data.keyResults || data.metrics || [],
        strategicAlignment: data.strategicAlignment || '',
        stakeholders: Array.isArray(data.stakeholders) ? data.stakeholders.filter(Boolean) : [],
        owner: data.owner || 'Unassigned',
        status: INITIATIVE_STATUS.NOT_STARTED,
        priority: data.priority || PRIORITY_LEVELS.MEDIUM,
        createdBy: 'chat-transfer',
        tags: ['from-chat'],
        metrics: data.metrics || []
      };
      
      // Create the initiative
      const initiative = await this.createInitiative(initiativeData);
      
      // Set as current for editing
      this.currentInitiative = initiative;
      
      // Emit transfer complete event
      EventBus.emit(AppEvents.INITIATIVE_TRANSFER_COMPLETE, initiative);
      
      // Show success message
      if (window.showToast) {
        window.showToast('Initiative transferred from chat successfully!', 'success');
      }
      
      return initiative;
      
    } catch (error) {
      console.error('Failed to transfer initiative from chat:', error);
      if (window.showToast) {
        window.showToast('Failed to transfer initiative: ' + error.message, 'error');
      }
      throw error;
    }
  }
  
  /**
   * Update initiative progress
   * @param {string} id - Initiative ID
   * @param {number} progress - Progress percentage (0-100)
   */
  async updateProgress(id, progress) {
    if (progress < 0 || progress > 100) {
      throw new Error('Progress must be between 0 and 100');
    }
    
    // Determine status based on progress
    let status;
    if (progress === 0) {
      status = INITIATIVE_STATUS.NOT_STARTED;
    } else if (progress === 100) {
      status = INITIATIVE_STATUS.COMPLETED;
    } else {
      status = INITIATIVE_STATUS.IN_PROGRESS;
    }
    
    return this.updateInitiative(id, { progress, status });
  }
  
  /**
   * Add risk to initiative
   * @param {string} id - Initiative ID
   * @param {Object} risk - Risk object
   */
  async addRisk(id, risk) {
    const initiative = this.getInitiative(id);
    if (!initiative) {
      throw new Error(`Initiative not found: ${id}`);
    }
    
    const risks = [...(initiative.risks || []), {
      id: this.generateId(),
      ...risk,
      createdAt: new Date().toISOString()
    }];
    
    return this.updateInitiative(id, { risks });
  }
  
  /**
   * Get initiative statistics
   * @returns {Object} Statistics
   */
  getStatistics() {
    const total = this.initiatives.length;
    const byStatus = {};
    const byPriority = {};
    const byOwner = {};
    
    // Count by status
    Object.values(INITIATIVE_STATUS).forEach(status => {
      byStatus[status] = this.initiatives.filter(i => i.status === status).length;
    });
    
    // Count by priority
    Object.values(PRIORITY_LEVELS).forEach(priority => {
      byPriority[priority] = this.initiatives.filter(i => i.priority === priority).length;
    });
    
    // Count by owner
    this.initiatives.forEach(i => {
      byOwner[i.owner] = (byOwner[i.owner] || 0) + 1;
    });
    
    // Calculate averages
    const avgProgress = total > 0 
      ? Math.round(this.initiatives.reduce((sum, i) => sum + (i.progress || 0), 0) / total)
      : 0;
    
    const avgKeyResults = total > 0
      ? Math.round(this.initiatives.reduce((sum, i) => sum + (i.keyResults?.length || 0), 0) / total)
      : 0;
    
    return {
      total,
      byStatus,
      byPriority,
      byOwner,
      avgProgress,
      avgKeyResults,
      completed: byStatus[INITIATIVE_STATUS.COMPLETED] || 0,
      atRisk: byStatus[INITIATIVE_STATUS.AT_RISK] || 0,
      completionRate: total > 0 
        ? Math.round((byStatus[INITIATIVE_STATUS.COMPLETED] || 0) / total * 100) 
        : 0
    };
  }
  

  
  /**
   * Generate unique ID
   */
  generateId() {
    return `init_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * Get service status
   */
  getStatus() {
    const stats = this.getStatistics();
    return {
      initialized: this.initialized,
      total: this.initiatives.length,
      ...stats,
      currentInitiative: this.currentInitiative?.id || null
    };
  }
  
  /**
   * Clear all initiatives (use with caution)
   */
  async clearAll() {
    if (!confirm('Are you sure you want to delete all initiatives? This cannot be undone.')) {
      return false;
    }
    
    this.initiatives = [];
    this.currentInitiative = null;
    
    // Update state and storage (via repo)
    try { storeDispatch(actions.setInitiatives([])); }
    catch (_) { AppState.setState({ initiatives: [] }); }
    initiativesRepo.clear();
    
    // Notify listeners
    EventBus.emit(AppEvents.INITIATIVES_CLEARED);
    console.log('⚠️ All initiatives cleared (including legacy storage keys)');
    
    return true;
  }
}

let causalGraphServicePromise = null;
async function ensureCausalGraphService() {
  if (!causalGraphServicePromise) {
    causalGraphServicePromise = import('./CausalGraphService.js').then(mod => mod.default);
  }
  return causalGraphServicePromise;
}

let canvasBusPromise = null;
async function refreshCanvas() {
  try {
    if (!canvasBusPromise) {
      canvasBusPromise = import('./graph/CanvasBus.js').then(mod => mod.default);
    }
    const bus = await canvasBusPromise;
    bus.refresh();
  } catch (_) {}
}

async function syncImpactMapIfNeeded(nextInitiative, previousInitiative) {
  if (!nextInitiative) return;
  if (previousInitiative && !impactFieldsChanged(previousInitiative, nextInitiative)) {
    return;
  }
  try {
    const svc = await ensureCausalGraphService();
    await svc.upsertImpactMapForInitiative(nextInitiative);
    await refreshCanvas();
  } catch (e) {
    console.warn('Impact map sync failed', e);
  }
}

function impactFieldsChanged(prev, next) {
  if (!prev) return true;
  if (stringValue(prev.objective) !== stringValue(next.objective)) return true;
  if (stringValue(prev.title) !== stringValue(next.title)) return true;
  if (stringValue(prev.owner) !== stringValue(next.owner)) return true;
  if (arraysDiffer(normalizeList(prev.stakeholders), normalizeList(next.stakeholders))) return true;
  if (arraysDiffer(normalizeList(prev.keyResults), normalizeList(next.keyResults))) return true;
  return false;
}

function stringValue(input) {
  return typeof input === 'string' ? input.trim() : String(input ?? '').trim();
}

function normalizeList(list) {
  if (!Array.isArray(list)) return [];
  return list.map((item) => stringValue(item)).filter(Boolean).sort();
}

function arraysDiffer(a, b) {
  if (a.length !== b.length) return true;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return true;
  }
  return false;
}

// Create singleton instance
const InitiativeService = new InitiativeServiceClass();

// Export service
export default InitiativeService;

// Attach to window in development
if (import.meta.env.DEV) {
  window.InitiativeService = InitiativeService;
  console.log('🔧 InitiativeService available at window.InitiativeService');
  console.log('💡 Commands: InitiativeService.getStatus(), InitiativeService.getStatistics()');
  
  // Test function
  window.testInitiative = async () => {
    const test = await InitiativeService.createInitiative({
      title: 'Test Initiative ' + Date.now(),
      description: 'This is a test initiative created for development',
      objective: 'Test the initiative service',
      keyResults: ['Verify CRUD operations', 'Test validation', 'Check storage'],
      owner: 'Test User',
      priority: 'high'
    });
    console.log('✅ Test initiative created:', test);
    return test;
  };
  
  console.log('💡 Test with: await window.testInitiative()');
}
